import type { IsoDateTime } from './common';

/** Who a kind of notification goes to: the seller's users, the warehouse's admins and supervisors, or everyone on its floor. */
export type NotificationAudience = 'seller' | 'warehouse' | 'floor';

/** Every kind of bildirim, with the words the settings screen shows for it. */
export const NOTIFICATION_KINDS = {
  'inbound.expected': { audience: 'warehouse', label: 'Yeni gönderi bekleniyor', hint: 'Bir satıcı depoya gönderi bildirdiğinde' },
  'inbound.discrepancy': { audience: 'warehouse', label: 'Mal kabulde sayı tutmadı', hint: 'Bildirilenden eksik ya da fazla gelen bir gönderi kapatıldığında' },
  'ops.problem': { audience: 'warehouse', label: 'Sistem sorunu', hint: 'Sistem durumu ekranına düşen kritik sorunlar' },
  'account.password_help': {
    audience: 'warehouse',
    label: 'Şifresini unutan kullanıcı',
    hint: 'Giriş ekranında "Şifremi unuttum" diyen biri: Kullanıcılar ekranından geçici şifre verin',
  },
  'order.carrier_changed': {
    audience: 'floor',
    label: 'Basılan etiketin kargo firması değişti',
    hint: 'Etiket basıldıktan sonra pazaryerinde kargo firması değişirse: yeni etiketi basıp eskisinin üstüne yapıştırın',
  },
  'order.cancelled_packed': {
    audience: 'floor',
    label: 'Paketlenen sipariş iptal edildi: KARGOYA VERMEYİN',
    hint: 'Paket sevkiyatta beklerken sipariş iptal edilirse: kuryeye vermeyin, açıp ürünleri Rafa iade göreviyle rafına koyun',
  },
  'order.line_cancelled': {
    audience: 'floor',
    label: 'Pazaryeri siparişin bir ürününü iptal etti',
    hint: 'Kalan ürünler varken: sipariş hazırlanmadıysa beklemeye alınır; toplandıysa ürün Rafa iade görevine düşer, paketlendiyse paket açılıp yeniden paketlenir',
  },
  'order.package_split': {
    audience: 'floor',
    label: 'Pazaryeri siparişi ayrı paketlere böldü',
    hint: 'Depo siparişi tek koli olarak hazırlıyorsa: hazırlanmadıysa beklemeye alınır; hazırlanıyorsa kargoya vermeden satıcıyla karar verin',
  },
  'order.cancelled_after_handover': {
    audience: 'floor',
    label: 'Kargoya verilen siparişi pazaryeri iptal etti',
    hint: 'Paket depodan çıktıktan sonra iptal edilirse: kurye almadıysa geri alın, aldıysa iade olarak dönmesini bekleyin',
  },
  'order.carrier_return': {
    audience: 'floor',
    label: 'Kargo paketi geri getiriyor (iade)',
    hint: 'Aras paketi iade olarak gösterince: paket gelince iade masasında kabul edin',
  },
  'order.taken_unpacked': {
    audience: 'floor',
    label: 'Kargo paketlemesi bitmemiş siparişi aldı',
    hint: 'Pazaryeri paketi kargoda gösterince: ürünleri toplanmış sipariş depoda paketlendi ve kargoya verildi sayılır',
  },
  'order.stock_waiting': {
    audience: 'floor',
    label: 'Sipariş stok bekliyor',
    hint: 'Ürünü depoda stokta olmayan bir sipariş gelince: ürün depodaysa mal kabulden stoğa girin; değilse satıcının gönderisi bekleniyor (Stok bekleyen siparişler)',
  },
  'inbound.closed': { audience: 'seller', label: 'Gönderiniz kabul edildi', hint: 'Eksik, fazla ve hasarlı gelenlerle; sayı tutmadıysa hangi üründe' },
  'order.carrier_return_seller': {
    audience: 'seller',
    label: 'Siparişiniz kargodan geri dönüyor',
    hint: 'Aras paketi iade olarak gösterince; depo paketi gelince iade olarak kabul eder',
  },
  'order.line_cancelled_seller': {
    audience: 'seller',
    label: 'Pazaryeri siparişinizin bir ürününü iptal etti',
    hint: 'Siparişin kalan ürünleri bekler: kalanlar gönderilmeyecekse siparişi pazaryerinden iptal edin',
  },
  'order.cancelled_after_handover_seller': {
    audience: 'seller',
    label: 'Kargoya verilen siparişiniz pazaryerinde iptal edildi',
    hint: 'Paket depodan çıktıktan sonra pazaryeri iptal ederse: depo kuryeden geri almaya çalışır, alamazsa paket iade olarak döner',
  },
  'order.carrier_check': {
    audience: 'seller',
    label: 'Etiketi basılan siparişin kargo firması değişti',
    hint: 'Pazaryerinde kargo firması değişince; değişikliği siz yapmadıysanız pazaryerinden kontrol edin',
  },
  'account.password_seller': {
    audience: 'seller',
    label: 'Kullanıcınız şifresini unuttu',
    hint: 'Firmanızdan biri "Şifremi unuttum" dediğinde: Kullanıcılar ekranından geçici şifre verin',
  },
  'order.held': {
    audience: 'seller',
    label: 'Sipariş bekliyor: eşleşmeyen ürün ya da pazaryerinde bulunamadı',
    hint: 'Ürünü eşleştirince sipariş kendiliğinden devam eder; pazaryerinin artık göstermediği sipariş depoda bekletilir, pazaryeri panelinden kontrol edin',
  },
  'order.backordered': {
    audience: 'seller',
    label: 'Sipariş için depoda stok yok',
    hint: 'Ürünü depoya göndermek için Depoya gönderiler’den gönderi oluşturun; stok gelince sipariş kendiliğinden hazırlanır',
  },
  'order.shipped_elsewhere': { audience: 'seller', label: 'Kendi gönderdiğiniz sipariş depoda iptal edildi', hint: 'Pazaryeri kargoda gösterince, depo ikinci kez göndermez' },
  'order.cancelled_in_depot': {
    audience: 'seller',
    label: 'Pazaryeri siparişi depoda iptal edildi',
    hint: 'Depo ya da siz panelden iptal edince: pazaryeri bunu bilmez, siparişi pazaryeri panelinden de iptal edin',
  },
  'marketplace.auth_failed': { audience: 'seller', label: 'Pazaryeri anahtarı reddedildi', hint: 'Siparişler çekilemez; anahtarları güncellemeniz gerekir' },
  'stock.push_failed': { audience: 'seller', label: 'Stok pazaryerine gönderilemedi', hint: 'Pazaryerindeki stok depodakinden farklı kalabilir' },
  'invoice.issued': { audience: 'seller', label: 'Hizmet faturası kesildi', hint: 'Depo hizmetlerinin aylık faturası' },
} as const satisfies Record<string, { audience: NotificationAudience; label: string; hint: string }>;

export type NotificationKind = keyof typeof NOTIFICATION_KINDS;

export function notificationKindsFor(audience: NotificationAudience): NotificationKind[] {
  return (Object.keys(NOTIFICATION_KINDS) as NotificationKind[]).filter((k) => NOTIFICATION_KINDS[k].audience === audience);
}

/** One bildirim of the signed-in person. */
export interface NotificationView {
  readonly id: string;
  readonly kind: NotificationKind;
  readonly title: string;
  readonly body: string | null;
  /** a screen of the panel ("#/asn/…") */
  readonly link: string | null;
  /** the seller it is about, for warehouse staff */
  readonly tenantCode: string | null;
  readonly createdAt: IsoDateTime;
  readonly readAt: IsoDateTime | null;
}

/** How many bildirimler a person has: the bell shows the unread ones, "Tümünü sil" asks about all of them. */
export interface NotificationCounts {
  readonly unread: number;
  readonly total: number;
}

/** After a deletion: how many went, and what is left. */
export interface NotificationsRemoved extends NotificationCounts {
  readonly deleted: number;
}

export interface NotificationSetting {
  readonly kind: NotificationKind;
  readonly label: string;
  readonly hint: string;
  readonly on: boolean;
}
